import { useEffect, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, H5, Divider } from "orcs-design-system";

import { useAllocationProject } from "src/contexts/global/GlobalContext";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";

import GroupsNavigationBase from "./GroupsNavigation";
import getTeamPath from "src/util/getTeamPath";
import { getSidebarTeamTreeLinkTo } from "./getTeamTreeLinkTo";
import LoadingBlock from "./LoadingBlock";
import useGroupTreeEventListeners from "./useGroupTreeEventListeners";
import { teamIdFromUrl } from "src/consts/urlPaths";

// Cast to bypass exactOptionalPropertyTypes on optional props passed from JS callers
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GroupsNavigation = GroupsNavigationBase as any;

type GroupsNavigationLoaderProps = {
  root?: object;
  treeData?: object | unknown[];
  rootGroupsLoading?: boolean;
  groupTypes?: Record<string, unknown>;
  hierarchyIds?: string[] | null;
  setHierarchyIds?: (ids: string[]) => void;
  sortOption?: string | boolean;
  /** Story/tests: skip router navigation when row/chevron also selects. */
  navigateTo?: (pathname: string, ids: string[]) => void;
  /** Story/tests: override link resolver without workspace providers. */
  getTeamLinkTo?: (node: {
    id: string;
    group?: { id?: string };
    [key: string]: unknown;
  }) => string | null;
};

const GroupsNavigationLoader = ({
  root,
  treeData,
  rootGroupsLoading,
  groupTypes,
  hierarchyIds,
  setHierarchyIds,
  sortOption,
  navigateTo: navigateToOverride,
  getTeamLinkTo: getTeamLinkToOverride,
}: GroupsNavigationLoaderProps) => {
  const history = useHistory();
  const location = useLocation();
  const workspace = useWorkspace();
  const activeAllocationProject = useAllocationProject();
  const activeNodeId = teamIdFromUrl(location.pathname);
  const [groupsTree, setGroupsTree] = useState<object | unknown[]>([]);

  const refreshTree = useCallback(() => {
    setGroupsTree((td) =>
      Array.isArray(td) ? [...td] : { ...(td as object) }
    );
  }, []);

  useGroupTreeEventListeners(root, groupTypes, refreshTree);

  const navigateTo = useCallback(
    (nextPathname: string, groupPathIds: string[]) => {
      if (navigateToOverride) {
        navigateToOverride(nextPathname, groupPathIds);
        return;
      }
      setHierarchyIds?.(groupPathIds);
      history.push(nextPathname);
    },
    [history, navigateToOverride, setHierarchyIds]
  );

  const getTeamLinkTo = useCallback(
    (node: { id: string; group?: { id?: string }; [key: string]: unknown }) => {
      if (getTeamLinkToOverride) {
        return getTeamLinkToOverride(node);
      }
      return getSidebarTeamTreeLinkTo(node, {
        getTeamDetailsPath: (group) =>
          getTeamPath({
            group,
            workspace,
            activeAllocationProject,
            pathname: location.pathname,
          }),
      });
    },
    [
      activeAllocationProject,
      getTeamLinkToOverride,
      location.pathname,
      workspace,
    ]
  );

  useEffect(() => {
    setGroupsTree(treeData ?? []);
  }, [treeData]);

  if (rootGroupsLoading) {
    return <LoadingBlock />;
  }

  return (
    <Box>
      <H5 fontWeight="bold">Teams</H5>
      {/* Match TagsNavigation (my="r") so the sticky panel X fits above the
          divider instead of overlapping it, and the first tree row isn't tight. */}
      <Box my="r">
        <Divider light />
      </Box>
      <GroupsNavigation
        root={root as never}
        treeData={groupsTree as never}
        navigateTo={navigateTo}
        refreshTree={refreshTree}
        groupTypes={groupTypes}
        sortOption={sortOption}
        getTeamLinkTo={getTeamLinkTo}
        {...(hierarchyIds != null ? { hierarchyIds } : {})}
        {...(activeNodeId != null ? { activeNodeId } : {})}
      />
    </Box>
  );
};

export default GroupsNavigationLoader;
